import { Trophy, RotateCcw, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { ScenarioChoice } from '@/hooks/use-complete-module';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatPercentage } from '@/lib/format';

export interface ConclusionChoice extends ScenarioChoice {
  max_points: number;
}

interface ConclusionPanelProps {
  choices: ConclusionChoice[];
  totalPoints: number;
  maxPoints: number;
  onComplete: () => void;
  onTryAgain: () => void;
  isCompleting: boolean;
  error?: string | undefined;
  nextModuleId: string | undefined;
}

export function ConclusionPanel({
  choices,
  totalPoints,
  maxPoints,
  onComplete,
  onTryAgain,
  isCompleting,
  error,
  nextModuleId,
}: ConclusionPanelProps) {
  const percentage = maxPoints > 0 ? (totalPoints / maxPoints) * 100 : 0;

  return (
    <Card>
      <CardHeader className="text-center">
        <div className="mx-auto mb-2 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
          <Trophy className="h-8 w-8 text-primary" />
        </div>
        <CardTitle>Scenario Complete</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="text-center">
          <p className="text-3xl font-bold">{formatPercentage(percentage)}</p>
          <p className="text-sm text-muted-foreground">
            {totalPoints} of {maxPoints} points
          </p>
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium">Decision Summary</p>
          {choices.map((choice, i) => (
            <div key={i} className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Decision {i + 1}</span>
              <span className="font-medium">
                {choice.points}/{choice.max_points} points
              </span>
            </div>
          ))}
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex flex-col gap-2 sm:flex-row">
          <Button onClick={onComplete} disabled={isCompleting} className="flex-1">
            Complete Module
          </Button>
          <Button variant="outline" onClick={onTryAgain} className="flex-1">
            <RotateCcw className="mr-2 h-4 w-4" />
            Try Again
          </Button>
          {nextModuleId && (
            <Button variant="secondary" asChild className="flex-1">
              <Link to={`/training/modules/${nextModuleId}`}>
                Next Module
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
